/**
 * Button Component
 *
 * A clickable button with variants, sizes, and loading state.
 * Styling is class-driven from ui-core recipe generated CSS.
 * Component follows Smoothie UI architecture.
 *
 * @module @smoothie-framework/ui/components
 */
import { createNodeId } from '../../render/node-id';
import { buildComponentClassNames, resolveResponsivePropValue } from '../../authoring/internal';
import { mergeStyleStrings } from '../../internal/style';
import { extractStaticText, normalizeChildrenToNodes } from '../../internal/children';
import { buildCommonUIProps, } from '../../authoring/common-props';
import { composeAsChildNode, resolveAsChildRoot } from '../../authoring/as-child';
import { Spinner } from '../spinner';
import { computed } from '../../state';
import { Show } from '../../control';
/**
 * Button component - clickable button with variants
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Click me
 * </Button>
 *
 * <Button variant="primary" loading>
 *   Submitting...
 * </Button>
 *
 * <Button variant="secondary" fullWidth>
 *   Full Width Button
 * </Button>
 * ```
 */
export function Button(props) {
    const hasExplicitVariant = hasExplicitResponsiveBaseValue(props.variant);
    const hasExplicitSize = hasExplicitResponsiveBaseValue(props.size);
    const { id, textStyle, variant = 'primary', size = 'md', icon, disabled = false, loading = false, loadingText, spinnerPlacement = 'start', spinnerSize, spinner, fullWidth = false, asChild = false, hideFrom, hideBelow, ariaLabel, ariaDescribedBy, ariaExpanded, ariaControls, className, style, children, leftIcon, rightIcon, 'data-ui': dataUi, type = 'button', form, onClick, onMouseEnter, onMouseLeave, onFocus, onBlur, } = props;
    const resolvedVariant = resolveResponsivePropValue(variant, 'primary');
    const resolvedSize = resolveResponsivePropValue(size, 'md');
    const normalizedChildren = normalizeChildrenToNodes(children);
    const asChildRoot = asChild ? resolveAsChildRoot('Button', children) : undefined;
    const buttonContentNodes = asChildRoot
        ? asChildRoot.text === undefined
            ? asChildRoot.children
            : normalizeChildrenToNodes(asChildRoot.text)
        : normalizedChildren;
    const hasButtonContent = buttonContentNodes.length > 0;
    const loadingTextNodes = normalizeChildrenToNodes(loadingText);
    const customSpinnerNodes = normalizeChildrenToNodes(spinner);
    const leftIconNodes = normalizeChildrenToNodes(leftIcon);
    const rightIconNodes = normalizeChildrenToNodes(rightIcon);
    assertValidExplicitIconComposition(leftIconNodes.length > 0, rightIconNodes.length > 0, hasButtonContent);
    const resolvedIcon = icon ?? inferButtonIconVariant(leftIconNodes.length > 0, rightIconNodes.length > 0, hasButtonContent);
    const baseClass = 'smth-button';
    const resolveLoading = () => typeof loading === 'function' ? Boolean(loading()) : loading;
    const loadingIsReactive = typeof loading === 'function';
    const classNameValue = loadingIsReactive
        ? computed(() => buildComponentClassNames(baseClass, { variant, size, loading: resolveLoading(), fullWidth, icon: resolvedIcon }, className, { fallbackVariants: { variant: 'primary', size: 'md' } }))
        : buildComponentClassNames(baseClass, { variant, size, loading, fullWidth, icon: resolvedIcon }, className, { fallbackVariants: { variant: 'primary', size: 'md' } });
    let childNodes = [];
    let textContent = extractStaticText(normalizedChildren);
    const hasExplicitIconContent = leftIconNodes.length > 0 || rightIconNodes.length > 0;
    const createLoadingContentNodes = () => {
        const spinnerNode = createLoadingSpinnerNode(baseClass, customSpinnerNodes, spinnerSize);
        const loadingContentNodes = loadingTextNodes.length > 0
            ? loadingTextNodes
            : normalizedChildren;
        if (spinnerPlacement === 'end') {
            return [...loadingContentNodes, spinnerNode];
        }
        return [spinnerNode, ...loadingContentNodes];
    };
    const createRestingContentNodes = () => hasExplicitIconContent
        ? [...leftIconNodes, ...normalizedChildren, ...rightIconNodes]
        : normalizedChildren;
    if (loadingIsReactive) {
        childNodes = [
            Show({
                when: resolveLoading,
                fallback: createRestingContentNodes(),
                children: createLoadingContentNodes(),
            }),
        ];
        textContent = undefined;
    }
    else if (loading) {
        childNodes = createLoadingContentNodes();
        textContent = undefined;
    }
    else if (hasExplicitIconContent) {
        childNodes = createRestingContentNodes();
        textContent = undefined;
    }
    else if (textContent === undefined) {
        childNodes = normalizedChildren;
    }
    const mergedStyle = mergeStyleStrings([], style);
    const resolvedDisabled = typeof disabled === 'function' || loadingIsReactive
        ? computed(() => {
            const disabledValue = typeof disabled === 'function' ? disabled() : disabled;
            return disabledValue || resolveLoading() ? 'true' : undefined;
        })
        : disabled || loading ? 'true' : undefined;
    const resolveDisabled = () => {
        const disabledValue = typeof disabled === 'function' ? disabled() : disabled;
        return Boolean(disabledValue || resolveLoading());
    };
    const shouldGuardAsChildClick = asChild && (onClick !== undefined || typeof disabled === 'function' || loadingIsReactive || Boolean(disabled) || Boolean(loading));
    const clickHandler = asChild
        ? shouldGuardAsChildClick
            ? (event) => {
                if (resolveDisabled()) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }
                return onClick?.(event);
            }
            : undefined
        : onClick;
    const commonButtonProps = {
        className: classNameValue,
        'data-loading': loadingIsReactive
            ? computed(() => resolveLoading() ? 'true' : undefined)
            : loading ? 'true' : undefined,
        'data-variant': resolvedVariant,
        'data-size': resolvedSize,
        'data-icon': resolvedIcon,
        ...(textStyle !== undefined && { textStyle }),
        ...(hideFrom !== undefined && { hideFrom }),
        ...(hideBelow !== undefined && { hideBelow }),
        ...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy }),
        ...(ariaExpanded !== undefined && { 'aria-expanded': ariaExpanded }),
        ...(ariaControls !== undefined && { 'aria-controls': ariaControls }),
        ...(ariaLabel !== undefined && { ariaLabel }),
        ...(clickHandler && { onClick: clickHandler }),
        ...(onMouseEnter && { onMouseEnter }),
        ...(onMouseLeave && { onMouseLeave }),
        ...(onFocus && { onFocus }),
        ...(onBlur && { onBlur }),
        ...buildCommonUIProps({
            id,
            style: mergedStyle,
            dataUi,
            defaultDataUi: 'Button',
        }),
    };
    const buttonProps = asChild
        ? {
            ...commonButtonProps,
            'aria-disabled': resolvedDisabled,
            'data-disabled': resolvedDisabled,
        }
        : {
            ...commonButtonProps,
            disabled: resolvedDisabled,
            type,
            ...(form && { form }),
        };
    if (asChild) {
        const composedChild = composeAsChildNode({
            componentType: 'Button',
            props: buttonProps,
            children: asChildRoot,
            explicitDataUi: dataUi,
            eventOrder: 'outer-first',
            transformChild: (child) => applyComposedButtonContent(child, {
                loading,
                loadingTextNodes,
                spinnerPlacement,
                spinnerSize,
                customSpinnerNodes,
                leftIconNodes,
                rightIconNodes,
            }),
        });
        const managesDisabledState = typeof disabled === 'function'
            || loadingIsReactive
            || Boolean(disabled)
            || Boolean(loading);
        const managesLoadingState = loadingIsReactive || Boolean(loading);
        const composedProps = {
            ...composedChild.props,
            ...(managesDisabledState && {
                'aria-disabled': resolvedDisabled,
                'data-disabled': resolvedDisabled,
            }),
            ...(managesLoadingState && {
                'data-loading': commonButtonProps['data-loading'],
            }),
        };
        const composedButtonMetadata = {
            _runtimeButtonExplicitVariant: hasExplicitVariant,
            _runtimeButtonExplicitSize: hasExplicitSize,
        };
        const managedDisabledIsReactive = typeof disabled === 'function' || loadingIsReactive;
        if (composedChild.type !== 'button') {
            const isAnchorHost = composedChild.type === 'a'
                || composedChild.type === 'Link'
                || composedProps['data-as'] === 'a';
            const hasLinkSchema = composedChild.type === 'Link'
                || composedChild._runtimeComposedSchemaTypes?.includes('Link') === true;
            const childHref = composedProps.href;
            const href = isAnchorHost && !hasLinkSchema && managesDisabledState
                ? managedDisabledIsReactive
                    ? computed(() => {
                        const childHrefValue = typeof childHref === 'function' ? childHref() : childHref;
                        return resolveDisabled() ? undefined : childHrefValue;
                    })
                    : undefined
                : childHref;
            return {
                ...composedChild,
                props: {
                    ...composedProps,
                    ...(isAnchorHost && { href }),
                },
                ...composedButtonMetadata,
            };
        }
        const childDisabled = composedProps.disabled;
        const nativeDisabled = managedDisabledIsReactive
            ? computed(() => {
                const childDisabledValue = typeof childDisabled === 'function'
                    ? childDisabled()
                    : childDisabled;
                return resolveDisabled() || childDisabledValue === true || childDisabledValue === 'true'
                    ? 'true'
                    : undefined;
            })
            : resolvedDisabled;
        return {
            ...composedChild,
            props: {
                ...composedProps,
                type: composedProps.type ?? type,
                ...(form && composedProps.form === undefined && { form }),
                ...(managesDisabledState && { disabled: nativeDisabled }),
            },
            ...composedButtonMetadata,
        };
    }
    return {
        id: createNodeId(),
        type: 'Button',
        props: buttonProps,
        children: childNodes,
        text: textContent,
        _runtimeButtonExplicitVariant: hasExplicitVariant,
        _runtimeButtonExplicitSize: hasExplicitSize,
    };
}
function applyComposedButtonContent(child, options) {
    const childContentNodes = child.text === undefined
        ? child.children
        : normalizeChildrenToNodes(child.text);
    const restingContentNodes = [
        ...options.leftIconNodes,
        ...childContentNodes,
        ...options.rightIconNodes,
    ];
    const loading = options.loading;
    const loadingIsReactive = typeof loading === 'function';
    const resolveLoading = () => loadingIsReactive
        ? Boolean(loading())
        : loading;
    if (!loadingIsReactive && !resolveLoading()) {
        if (options.leftIconNodes.length === 0 && options.rightIconNodes.length === 0) {
            return child;
        }
        return {
            ...child,
            children: restingContentNodes,
            text: undefined,
        };
    }
    const loadingContentNodes = options.loadingTextNodes.length > 0
        ? options.loadingTextNodes
        : childContentNodes;
    const spinnerNode = createLoadingSpinnerNode('smth-button', options.customSpinnerNodes, options.spinnerSize);
    const resolvedLoadingContent = options.spinnerPlacement === 'end'
        ? [...loadingContentNodes, spinnerNode]
        : [spinnerNode, ...loadingContentNodes];
    const children = loadingIsReactive
        ? [Show({
                when: resolveLoading,
                fallback: restingContentNodes,
                children: resolvedLoadingContent,
            })]
        : resolveLoading()
            ? resolvedLoadingContent
            : restingContentNodes;
    return {
        ...child,
        children,
        text: undefined,
    };
}
function assertValidExplicitIconComposition(hasLeftIcon, hasRightIcon, hasChildren) {
    if (hasLeftIcon && hasRightIcon && !hasChildren) {
        throw new Error('Button does not support rendering both leftIcon and rightIcon without visible text. Use one icon for icon-only buttons or add button content.');
    }
}
function inferButtonIconVariant(hasLeftIcon, hasRightIcon, hasChildren) {
    if (!hasLeftIcon && !hasRightIcon) {
        return undefined;
    }
    if (!hasChildren) {
        return 'only-icon';
    }
    if (hasLeftIcon && hasRightIcon) {
        return 'icon-both';
    }
    return hasLeftIcon ? 'icon-left' : 'icon-right';
}
function hasExplicitResponsiveBaseValue(value) {
    if (value === undefined) {
        return false;
    }
    if (Array.isArray(value)) {
        return value.length > 0 && value[0] !== undefined;
    }
    if (typeof value === 'object' && value !== null) {
        return Object.prototype.hasOwnProperty.call(value, 'base')
            && value.base !== undefined;
    }
    return true;
}
/**
 * Default props for Button component
 */
export const defaultButtonProps = {
    variant: 'primary',
    size: 'md',
    disabled: false,
    loading: false,
    spinnerPlacement: 'start',
    fullWidth: false,
    type: 'button',
};
function createLoadingSpinnerNode(baseClass, customSpinnerNodes, spinnerSize) {
    if (customSpinnerNodes.length === 0) {
        return Spinner({
            size: spinnerSize ?? 'sm',
            className: `${baseClass}__spinner ${baseClass}-spinner`,
        });
    }
    return {
        id: createNodeId(),
        type: 'span',
        props: {
            className: `${baseClass}__spinner ${baseClass}-spinner`,
            'data-ui': 'ButtonSpinner',
        },
        children: customSpinnerNodes,
    };
}
//# sourceMappingURL=Button.js.map